import { useState, useEffect } from "react";
import { useXS } from "@ww-digital/xs-sdk-react";
import { parse, stringify } from "querystringify";
import t from "../../utils/translate";
import { UrlSlugProvider } from "../UrlSlugContext";
import { getSavedStateKey } from "../../utils/branching";
import {
  ALL_SLUGS,
  SLUG_DEFAULT,
  SLUG_CLINICAL,
  SLUG_ELI,
  SLUG_UNI1,
  SLUG_UNI2,
  SLUG_UNI3,
  SLUG_UNI4
} from "../../config/global";

const urlParams = parse(window.location.search);

// These are not used anymore. Here for making redirects
// from them to new URLs.
const defaultAssessmentParam = !!urlParams.def;
const eligibilityParam = !!urlParams.eli;
const isSequenceParam = !!urlParams.isSequence;
const isUni1Param = !!urlParams.uni1;
const isUni2Param = !!urlParams.uni2;

// Delete old query params.
delete urlParams.def;
delete urlParams.eli;
delete urlParams.isSequence;
delete urlParams.uni1;
delete urlParams.uni2;

const domainInterpolations = url => {
  if (window.ww.envSubdomain === "qat2.") {
    if (window.ww.isLocal) {
      return url.replace(
        "www.weightwatchers.com",
        "assessment.local.weightwatchers.com:3000"
      );
    } else {
      return url.replace(
        "www.weightwatchers.com",
        "www.qat2.weightwatchers.com"
      );
    }
  }

  // Prod.
  return url;
};

const slugToUrlMapping = {
  [SLUG_DEFAULT]: domainInterpolations(t("URL_DEFAULT_REDIRECT")),
  [SLUG_CLINICAL]: domainInterpolations(t("URL_FIRST_PAGE_CLINICAL")),
  [SLUG_ELI]: domainInterpolations(t("URL_FIRST_PAGE_ELI")),
  [SLUG_UNI1]: domainInterpolations(t("URL_FIRST_PAGE_UNI1")),
  [SLUG_UNI2]: domainInterpolations(t("URL_FIRST_PAGE_UNI2")),
  [SLUG_UNI3]: domainInterpolations(t("URL_FIRST_PAGE_UNI3")),
  [SLUG_UNI4]: domainInterpolations(t("URL_FIRST_PAGE_UNI4"))
};

export function redirectToFirstPage(slug) {
  const redirectTo = slugToUrlMapping[slug];
  window.location.href = `${redirectTo}${stringify(urlParams, true)}`;
}

export const isUniTestUrl = urlSlug =>
  urlSlug === SLUG_DEFAULT || urlSlug === SLUG_UNI3 || urlSlug === SLUG_UNI4;

const UrlValidator = ({ children }) => {
  const [baseUrl, setBaseUrl] = useState(null);
  const [urlSlug, setUrlSlug] = useState(null);
  const [appState, setAppState] = useState(null);
  const [queryString, setQueryString] = useState(null);
  const { attributes = {} } = useXS(t("ONE_WW_UNIFIED_QUIZ_AB_TEST_NAME"));
  const { SLUG_UNI_TEST = null } = attributes;

  useEffect(() => {
    if (
      defaultAssessmentParam ||
      eligibilityParam ||
      isSequenceParam ||
      isUni1Param ||
      isUni2Param
    ) {
      if (defaultAssessmentParam) {
        redirectToFirstPage(SLUG_DEFAULT);
      } else if (eligibilityParam) {
        redirectToFirstPage(SLUG_ELI);
      } else if (isSequenceParam) {
        redirectToFirstPage(SLUG_CLINICAL);
      } else if (isUni1Param) {
        redirectToFirstPage(SLUG_UNI1);
      } else if (isUni2Param) {
        redirectToFirstPage(SLUG_UNI2);
      }

      return;
    }

    const path = window.location.pathname;
    let basePath = window.wwBasePath.getAttribute("href");
    // On local basePath doesn't have trailing slash.
    // On other envs it has though.
    if (basePath[basePath.length - 1] !== "/") {
      basePath = `${basePath}/`;
    }

    // Remove base path from path.
    const cleanPathWithSlug = path.replace(basePath, "");
    const slugAndPath = cleanPathWithSlug.split("/");

    const [urlSlug, cleanPath] = slugAndPath;

    if (!urlSlug) {
      redirectToFirstPage(SLUG_DEFAULT);
      return;
    } else if (urlSlug && !cleanPath) {
      if (!ALL_SLUGS.includes(urlSlug)) {
        redirectToFirstPage(SLUG_DEFAULT);
        return;
      } else {
        redirectToFirstPage(urlSlug);
        return;
      }
    } else if (urlSlug && cleanPath && !ALL_SLUGS.includes(urlSlug)) {
      redirectToFirstPage(SLUG_DEFAULT);
      return;
    }

    const baseUrl = `${basePath}${urlSlug}`;
    let state = window.sessionStorage.getItem(
      getSavedStateKey(cleanPath, baseUrl)
    );

    const specialBundleId = new URLSearchParams(urlParams).get("sbi");

    if (specialBundleId) {
      sessionStorage.setItem("sbi", specialBundleId);
    }

    const specialCompoundingBundleId = new URLSearchParams(urlParams).get(
      "scbi"
    );

    if (specialCompoundingBundleId) {
      sessionStorage.setItem("scbi", specialCompoundingBundleId);
    }

    if (!state) {
      setUrlSlug(urlSlug);
      setBaseUrl(baseUrl);
      setQueryString(stringify(urlParams, true));

      return;
    }

    state = JSON.parse(state);

    setUrlSlug(urlSlug);
    setBaseUrl(baseUrl);
    setQueryString(state.queryString);
    setAppState(state);
  }, []);

  if (!urlSlug || !baseUrl) {
    return null;
  }

  if (
    isUniTestUrl(urlSlug) &&
    !window.location.search.includes("xs_redirect=false") &&
    SLUG_UNI_TEST &&
    SLUG_UNI_TEST !== urlSlug &&
    (SLUG_UNI_TEST !== SLUG_DEFAULT ||
      (SLUG_UNI_TEST == SLUG_DEFAULT && SLUG_DEFAULT !== urlSlug))
  ) {
    redirectToFirstPage(SLUG_UNI_TEST);
  }

  // There query params are used.
  const urlParamsToUse = parse(queryString);
  const coreUrlParam = urlParamsToUse.coreurl;
  const eliUrlParam = urlParamsToUse.eliurl;

  const context = {
    urlSlug,
    baseUrl,
    queryString,
    setQueryString,
    appState,
    coreUrlParam,
    eliUrlParam
  };

  return <UrlSlugProvider value={context}>{children}</UrlSlugProvider>;
};

export default UrlValidator;
